import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './HoverImage.css';

const HoverPreview = ({ src, alt, x, y, zoom }) => {
    // Prevent going off screen (basic logic)
    // If x is > window width / 2, show to left. 
    // This is a simplification, can be improved.
    const isRightSide = x > window.innerWidth / 2;
    const isBottomHalf = y > window.innerHeight / 2;
    
    const style = {
        top: isBottomHalf ? 'auto' : y + 20,
        bottom: isBottomHalf ? (window.innerHeight - y + 20) : 'auto',
        left: isRightSide ? 'auto' : x + 20,
        right: isRightSide ? (window.innerWidth - x + 20) : 'auto',
        transform: `scale(${zoom})`,
        transformOrigin: `${isBottomHalf ? 'bottom' : 'top'} ${isRightSide ? 'right' : 'left'}`,
    };

    const handleError = (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = '/api/assets/placeholder.png';
    };

    return createPortal(
        <div className="hover-preview-tooltip" style={style}>
            <img src={src} alt={alt} onError={handleError} />
            {zoom > 1 && <div className="zoom-badge">{Math.round(zoom * 100)}%</div>}
        </div>,
        document.body
    );
};

const HoverImage = ({ src, thumbSrc, alt, className, style, wrapperClassName, wrapperStyle }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [currentSrc, setCurrentSrc] = useState(() => thumbSrc || src);

    useEffect(() => {
        setCurrentSrc(thumbSrc || src);
    }, [src, thumbSrc]);

    const handleMouseMove = (e) => {
        setCoords({ x: e.clientX, y: e.clientY });
    };

    const handleWheel = (e) => {
        if (!isHovering) return;
        
        // Prevent default scrolling only if we are effectively zooming
        // Note: In React, checking if we can preventDefault is tricky with passive events standard
        // but for now let's just update state.
        
        const delta = Math.sign(e.deltaY) * -1; // -1 for up (zoom in), 1 for down (zoom out)
        
        setZoom(prev => {
            const step = 0.5;
            let newZoom = prev + (delta * step);
            // Clamp between 1x and 5x
            return Math.min(Math.max(1, newZoom), 5); 
        });
    };

    return (
        <>
            <img 
                src={currentSrc}
                alt={alt} 
                className={className} 
                style={style}
                onError={(e) => { 
                    e.currentTarget.onerror = null;
                    // If we failed to load the thumbnail, fall back to original
                    if (thumbSrc && currentSrc === thumbSrc) {
                        setCurrentSrc(src);
                        e.currentTarget.src = src;
                        return;
                    }
                    e.currentTarget.src = '/api/assets/placeholder.png'; 
                }}
                onMouseEnter={(e) => { setIsHovering(true); handleMouseMove(e); }}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => { setIsHovering(false); setZoom(1); }}
                onWheel={handleWheel}
            />
            {isHovering && <HoverPreview src={src} alt={alt} x={coords.x} y={coords.y} zoom={zoom} />}
        </>
    );
};

export default HoverImage;
